import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertTriangle, Clock, CalendarDays, CheckCircle2, PhoneCall, MessageCircle,
  Loader2, AlarmClockOff, ArrowRight, Inbox,
} from "lucide-react";
import { toast } from "sonner";

const SUPERVISOR_ROLES = ["super_admin", "dean"];

const TABS = [
  { key: "sla_breaches", label: "SLA Alerts" },
  { key: "today", label: "Due Today" },
  { key: "overdue", label: "Overdue" },
  { key: "upcoming", label: "Upcoming" },
  { key: "completed", label: "Completed" },
];

const OUTCOMES = [
  { key: "connected", label: "Connected" },
  { key: "busy", label: "Busy" },
  { key: "callback_requested", label: "Callback" },
  { key: "not_interested", label: "Not Interested" },
  { key: "wrong_number", label: "Wrong Number" },
];

const STAGES = [
  { key: "lead", label: "Lead" },
  { key: "application_initiated", label: "Application Initiated" },
  { key: "doc_verified", label: "Docs Verified" },
  { key: "offer_issued", label: "Offer Issued" },
  { key: "fee_paid", label: "Fee Paid" },
  { key: "enrolled", label: "Enrolled" },
];

const TASK_TYPE_LABEL = {
  sla_first_contact: "SLA first contact",
  follow_up_call: "Follow-up call",
  doc_discrepancy_help: "Document help",
  document_help: "Document help",
  fee_reminder: "Fee reminder",
};

const PRIORITY_STYLE = {
  urgent: "bg-red-100 text-red-800",
  high: "bg-amber-100 text-amber-800",
  medium: "bg-blue-100 text-blue-700",
  low: "bg-slate-100 text-slate-600",
};

function relTime(ms) {
  const mins = Math.max(1, Math.round(Math.abs(ms) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

function dueLabel(iso) {
  if (!iso) return { text: "No due date", overdue: false };
  const diff = new Date(iso).getTime() - Date.now();
  return diff < 0
    ? { text: `Breached by ${relTime(diff)}`, overdue: true }
    : { text: `Due in ${relTime(diff)}`, overdue: false };
}

function toLocalInput(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

const METRICS = [
  { key: "sla_breaches", label: "SLA Breaches", icon: AlertTriangle, tone: "text-red-600", ring: "border-red-200 bg-red-50" },
  { key: "overdue", label: "Overdue", icon: Clock, tone: "text-amber-600", ring: "border-amber-200 bg-amber-50" },
  { key: "due_today", label: "Due Today", icon: CalendarDays, tone: "text-blue-600", ring: "border-blue-200 bg-blue-50" },
  { key: "completed_today", label: "Completed Today", icon: CheckCircle2, tone: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50" },
];

export default function CounselorTasks() {
  const { viewRole } = useAuth();
  const isSupervisor = SUPERVISOR_ROLES.includes(viewRole);

  const [tab, setTab] = useState("today");
  const [priority, setPriority] = useState("");
  const [counselorId, setCounselorId] = useState("");
  const [counselors, setCounselors] = useState([]);
  const [data, setData] = useState({ summary: {}, tasks: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // task being disposed

  useEffect(() => {
    if (!isSupervisor) return;
    api.get("/admin/assignment/counselors").then((r) => setCounselors(r.data || [])).catch(() => {});
  }, [isSupervisor]);

  const fetchQueue = useCallback(() => {
    setLoading(true);
    const params = { tab, limit: 100 };
    if (priority) params.priority = priority;
    if (isSupervisor && counselorId) params.counselor_id = counselorId;
    return api.get("/tasks/queue", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load tasks"))
      .finally(() => setLoading(false));
  }, [tab, priority, counselorId, isSupervisor]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const summary = data.summary || {};

  return (
    <div data-testid="tasks-page" className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Task Board</h1>
          <p className="text-sm text-slate-500 mt-1">Daily follow-up queue with 2-hour first-contact SLA tracking.</p>
        </div>
        {isSupervisor && (
          <div className="flex items-center gap-2" data-testid="supervisor-bar">
            <span className="text-xs font-medium text-slate-500">Viewing</span>
            <Select value={counselorId || "all"} onValueChange={(v) => setCounselorId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[220px]" data-testid="counselor-scope-select">
                <SelectValue placeholder="Entire team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Entire team</SelectItem>
                {counselors.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.email || c.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {METRICS.map((m) => (
          <Card key={m.key} className={`p-4 border ${m.ring}`} data-testid={`metric-${m.key}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <m.icon className={`w-4 h-4 ${m.tone}`} /> {m.label}
            </div>
            <div className={`mt-2 font-mono text-2xl font-bold ${m.tone}`}>{summary[m.key] ?? 0}</div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const count = t.key === "completed" ? summary.completed_today
            : t.key === "today" ? summary.due_today : summary[t.key];
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                on ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
              {count ? <span className="ml-1.5 text-xs text-slate-400">({count})</span> : null}
            </button>
          );
        })}
        <div className="ml-auto">
          <Select value={priority || "any"} onValueChange={(v) => setPriority(v === "any" ? "" : v)}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="priority-filter">
              <SelectValue placeholder="Any priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any priority</SelectItem>
              {["urgent", "high", "medium", "low"].map((p) => (
                <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : data.tasks.length === 0 ? (
        <div className="py-16 flex flex-col items-center gap-2 text-slate-400" data-testid="tasks-empty">
          <Inbox className="w-8 h-8" />
          <p className="text-sm">Nothing in this queue.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.tasks.map((t) => (
            <TaskCard key={t.id} task={t} onLog={() => setActive(t)} onChanged={fetchQueue} />
          ))}
        </div>
      )}

      <DispositionModal
        task={active}
        onClose={() => setActive(null)}
        onDone={(next) => { if (next) { setActive(next); } else { setActive(null); } fetchQueue(); }}
      />
    </div>
  );
}

function TaskCard({ task, onLog, onChanged }) {
  const due = dueLabel(task.due_date);
  const done = task.status === "completed";
  const phone = task.candidate_phone || "";
  const digits = phone.replace(/\D/g, "").slice(-10);

  const snooze = async (hours) => {
    try {
      await api.patch(`/tasks/${task.id}/snooze`, { snooze_hours: hours });
      toast.success(`Snoozed ${hours >= 24 ? `${hours / 24}d` : `${hours}h`}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Snooze failed");
    }
  };

  return (
    <Card className="p-4 border-slate-200 flex flex-col gap-3" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{task.candidate_name || "Unknown candidate"}</div>
          <div className="text-xs text-slate-500 truncate">
            {[task.lead_source, task.course].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <Badge className={`shrink-0 capitalize ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium}`}>
          {task.priority}
        </Badge>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">{TASK_TYPE_LABEL[task.task_type] || task.task_type}</span>
        <span className="text-slate-300">•</span>
        <span className={done ? "text-slate-400" : due.overdue ? "text-red-600 font-semibold" : "text-slate-600"}>
          {done ? "Completed" : due.text}
        </span>
      </div>

      {task.notes ? (
        <p className="text-sm text-slate-600 line-clamp-2">{task.notes}</p>
      ) : null}

      {done && task.disposition?.outcome ? (
        <Badge variant="secondary" className="w-fit capitalize">{task.disposition.outcome.replace(/_/g, " ")}</Badge>
      ) : null}

      <div className="flex items-center gap-1.5 mt-auto pt-1">
        <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Call"
          disabled={!digits}
          onClick={() => digits && (window.location.href = `tel:${phone}`)}>
          <PhoneCall className="w-4 h-4 text-slate-500" />
        </Button>
        <Button variant="outline" size="icon" className="h-8 w-8" aria-label="WhatsApp"
          disabled={!digits}
          onClick={() => digits && window.open(`https://wa.me/91${digits}`, "_blank")}>
          <MessageCircle className="w-4 h-4 text-emerald-600" />
        </Button>
        {!done && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Snooze" data-testid={`snooze-${task.id}`}>
                <AlarmClockOff className="w-4 h-4 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => snooze(1)}>Snooze 1 hour</DropdownMenuItem>
              <DropdownMenuItem onClick={() => snooze(4)}>Snooze 4 hours</DropdownMenuItem>
              <DropdownMenuItem onClick={() => snooze(24)}>Snooze 1 day</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {!done && (
          <Button className="ml-auto h-8 bg-brand" data-testid={`log-outcome-${task.id}`} onClick={onLog}>
            Log Outcome
          </Button>
        )}
      </div>
    </Card>
  );
}

function DispositionModal({ task, onClose, onDone }) {
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [followup, setFollowup] = useState("");
  const [stage, setStage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setOutcome(""); setNotes(""); setFollowup(""); setStage("");
  }, [task?.id]);

  if (!task) return null;

  const preset = (hours) => {
    const d = new Date(Date.now() + hours * 3600 * 1000);
    setFollowup(toLocalInput(d));
  };

  const submit = async () => {
    if (!outcome) { toast.error("Pick an outcome"); return; }
    setBusy(true);
    try {
      const body = { outcome, notes: notes.trim() || undefined };
      if (followup) body.next_followup_at = new Date(followup).toISOString();
      if (stage) body.update_stage = stage;
      const r = await api.post(`/tasks/${task.id}/disposition`, body);
      toast.success("Outcome logged");
      onDone(r.data?.next_task || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not log outcome");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="disposition-modal">
        <DialogHeader>
          <DialogTitle>Log outcome — {task.candidate_name || "candidate"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium mb-1.5">Outcome</div>
            <div className="flex flex-wrap gap-2">
              {OUTCOMES.map((o) => (
                <button
                  key={o.key}
                  data-testid={`outcome-${o.key}`}
                  onClick={() => {
                    setOutcome(o.key);
                    if (o.key === "callback_requested" && !followup) preset(24);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    outcome === o.key
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-1.5">Notes</div>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed / next step" data-testid="disposition-notes" />
          </div>

          <div>
            <div className="text-sm font-medium mb-1.5">Next follow-up</div>
            <input
              type="datetime-local"
              value={followup}
              onChange={(e) => setFollowup(e.target.value)}
              data-testid="disposition-followup"
              className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm"
            />
            <div className="flex gap-2 mt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => preset(24)}>+24h</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => preset(48)}>+48h</Button>
              {followup && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setFollowup("")}>Clear</Button>
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium mb-1.5">Update lead stage <span className="text-slate-400 font-normal">(optional)</span></div>
            <Select value={stage || "none"} onValueChange={(v) => setStage(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="disposition-stage"><SelectValue placeholder="No change" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No change</SelectItem>
                {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" onClick={submit} disabled={busy || !outcome} data-testid="disposition-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit &amp; Advance <ArrowRight className="w-4 h-4 ml-1.5" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
